import {
  IHRDatasetInfo,
  IHRSettings,
} from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { forwardRef, Ref, useImperativeHandle, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { HttpCancel } from 'core/classes/http'
import Logger from 'core/utils/Logger'
import { useActualsTypes } from 'partner360/components/actualsTypeButtons'
import { useCurrencies } from 'partner360/components/currenciesButtons'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import {
  selectFinancialYear,
  selectPartnerAccess,
} from '../../../../../features/partner360/configuration'
import { IJBPPlanning } from '../models/IJBPPlanning'
import { requestJBPActualsAndTargetsSettings } from '../services/requestJBPActualsAndTargetsSettings'
import { requestJBPComposedReport } from '../services/requestJBPComposedReport'

import { PlanningError } from './components/planningError/PlanningError'
import { RegionalDistributorWarning } from './components/regionalDistributorWarning/RegionalDistributorWarning'
import { IS_ALWAYS_SHOW_ACTUALS } from './config'
import { CategoryPlanningStandard } from './lookAndFeel/CategoryPlanningStandard'
import { TotalCalcPlanningStandard } from './lookAndFeel/TotalCalcPlanningStandard'
import { TSplitType } from './models'
import { IComposedFilterForPlanning } from './models/IComposedFilterForPlanning'
import { IPlanningRef } from './models/IPlanningRef'
import tr from './translations'

import styles from './Planning.module.scss'

interface IProps {
  widgetParams: IObjectAny
  metrics: MeasureController
  pid: string
  readOnly?: boolean
}

export const ComposedReportPlanning = forwardRef((props: IProps, ref: Ref<IPlanningRef>) => {
  useImperativeHandle(ref, () => ({ updatePlanning }))

  /** -------------------- State ----------------------- */

  const [offerings, setOfferings] = useState<string[]>([])
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [hrDataSets, setHRDataSets] = useState<IHRDatasetInfo[]>([])
  const [hrFormats, setHRFormats] = useState<IFormActualsTargetsSettings[]>([])
  const [isContractualPlanningEnable, setIsContractualPlanningEnable] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isNoData, setIsNoData] = useState(false)
  const [isDataError, setIsDataError] = useState(false)
  const [isRegionalPlanning, setIsRegionalPlanning] = useState(true)
  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false)
  const [isShowActuals, setIsShowActuals] = useState(false)
  const [isShowOnlyCompletedPlans, setIsShowOnlyCompletedPlans] = useState(true)
  const [isShowOnlyCompletedManuallyChanged, setIsShowOnlyCompletedManuallyChanged] =
    useState(false)
  const [isShowQuota, setIsShowQuota] = useState(false)
  const [planningData, setPlanningData] = useState<IJBPPlanning[]>([])
  const [originalActualsValueType, setOriginalActualsValueType] = useState('')
  const [originalCurrency, setOriginalCurrency] = useState('')
  const [composedReportFYear, setComposedReportFYear] = useState(0)
  const [splitType, setSplitType] = useState<TSplitType>('Total')
  const [isShowComposedSoB, setIsShowComposedSoB] = useState(false)
  const [isShowPrevTargets, setIsShowPrevTargets] = useState(false)

  /** -------------------- Hooks ----------------------- */

  const fYear = useSelector(selectFinancialYear)
  const partnerId = usePartnerId(props.widgetParams, props.pid)
  const { startLoadData, stopLoadData } = useMeasureController(props.metrics)
  const { setCurrency } = useCurrencies()
  const { onChangeActualsType } = useActualsTypes()
  const { isRegionalDistributor } = useSelector(selectPartnerAccess)
  const t = useSafetyTranslation()

  /** -------------------- Variables ----------------------- */

  const planningSettingsWOSummary = hrDataSets.filter((d) => !d.SummaryProgram)
  const isShowLoader = !isSettingsLoaded || !isDataLoaded
  const isShowCancelRequest = isInitialized && isShowLoader
  const isHideWidget = !isInitialized

  /** -------------------- Methods ----------------------- */

  const setIsShowOnlyCompletedPlansWrapper = (
    isShowOnlyCompletedPlans: boolean,
    isManualChanged: boolean
  ) => {
    if (isManualChanged) {
      setIsShowOnlyCompletedPlans(isShowOnlyCompletedPlans)
      setIsShowOnlyCompletedManuallyChanged(true)
    } else if (!isShowOnlyCompletedManuallyChanged) {
      setIsShowOnlyCompletedPlans(isShowOnlyCompletedPlans)
    }
  }

  const fetchPlanningSettings = async (filters: string, cancelObject?: HttpCancel) => {
    setIsSettingsLoaded(false)

    const settingsResponse = await requestJBPActualsAndTargetsSettings<IFormActualsTargetsSettings>(
      partnerId,
      fYear,
      undefined,
      filters,
      cancelObject
    )
    const settingsData = settingsResponse?.Rows || []
    setIsNoData(!settingsData.length)

    if (settingsData.length) {
      setHRFormats(settingsData)

      let category = ''
      let offering = ''
      const offerings: string[] = []
      const categoriesSettings: IHRDatasetInfo[] = []
      settingsData.forEach((el) => {
        if (offering !== el.ProgramType && !offerings.find((o) => o === el.ProgramType)) {
          offerings.push(el.ProgramType)
          offering = el.ProgramType
        }

        let settings: IHRSettings = {}
        try {
          settings = JSON.parse(el.Settings)
        } catch {
          Logger.error('error to parse settings, ComposedReportPlanning')
        }

        if (
          category !== el.Category &&
          !categoriesSettings.find((hi) => hi.category === el.Category)
        ) {
          category = el.Category

          // Temporally hide MPS Summary Chart
          if (!el.SummaryProgram) {
            categoriesSettings.push({
              category: category,
              categoryTranslation: t(el.Category),
              subCategory: el.SubCategory,
              colors: (el.Colors || '').split(';'),
              ProgramType: el.ProgramType,
              SummaryProgram: !!el.SummaryProgram,
              Settings: settings,
              IsContractualsAvailable: el.IsContractualsAvailable,
              IsSoBAvailable: el.IsSoBAvailable,
              IsSoBTotalCalc: el.IsSoBTotalCalc,
              IsUnitsAvailable: el.IsUnitsAvailable,
              IsHideFinancial: el.IsHideFinancial,
              IsOnlyQuarterlyPlanning: el.IsOnlyQuarterlyPlanning,
              IsMainHierarchy: el.IsMainHierarchy,
              MandatoryType: el.MandatoryType,
              MandatoryByFlags: el.MandatoryByFlags,
              UnitsMandatoryByFlags: el.UnitsMandatoryByFlags,
            })
          }
        }
      })
      setOfferings(offerings)
      setHRDataSets(categoriesSettings)
    }

    setIsSettingsLoaded(true)
  }

  const setOriginalSwitcherValues = (allPlanningData: IJBPPlanning[]) => {
    // Set Currency and ActualsValueType by first Targets data row
    const targetData = allPlanningData.filter((el) => !!el.IsTarget)
    if (targetData.length) {
      if (targetData[0].Currency) {
        setCurrency(targetData[0].Currency)
        setOriginalCurrency(targetData[0].Currency)
      }
      if (targetData[0].NetNdp) {
        onChangeActualsType(targetData[0].NetNdp as ActualsValueTypes)
        setOriginalActualsValueType(targetData[0].NetNdp)
      }
    }
  }

  const getFilterFYear = (filters: string) => {
    let composedFilters: IComposedFilterForPlanning = {}

    try {
      composedFilters = JSON.parse(filters) as IComposedFilterForPlanning
    } catch {
      Logger.error('error to parse filters, ComposedReportPlanning')
    }

    return composedFilters?.FYear
  }

  /** -------------------- User actions handlers ----------------------- */

  const updatePlanning = async (filters: string) => {
    setIsDataLoaded(false)
    setIsInitialized(true)
    startLoadData()

    setHRDataSets([])
    setHRFormats([])

    fetchPlanningSettings(filters, cancelObject)

    const planningResult = await requestJBPComposedReport<IJBPPlanning>(filters, cancelObject)
    const allPlanningData = planningResult?.Rows || []

    setIsNoData(!allPlanningData.length)
    setIsDataError(!!planningResult?.Message)

    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)
    const filterFYear = getFilterFYear(filters)
    setComposedReportFYear(filterFYear || 0)

    stopLoadData()
    setIsDataLoaded(true)
  }

  const onApplyChanges = (data: IJBPPlanning[]) => {
    const allPlanningData = data || []
    setPlanningData(allPlanningData)
    setOriginalSwitcherValues(allPlanningData)
  }

  const cancelDataRequest = () => {
    if (cancelObject) {
      cancelObject.cancel()
    }
  }

  /** -------------------- Effects ----------------------- */

  const cancelObject = useMemo(() => new HttpCancel(), [])

  /** -------------------- Render ----------------------- */

  if (isHideWidget) {
    return <></>
  }

  if (isNoData) {
    return <PlanningError text={isDataError ? tr.dataError : tr.noDataWarning} />
  }

  return (
    <div className={styles.root}>
      {isShowCancelRequest && (
        <Button onClick={cancelDataRequest}>
          <Translation text="jbp-cancel-request" span />
        </Button>
      )}

      <div className={styles.root}>
        <LoaderOverlay show={isShowLoader} />

        {isRegionalDistributor && (
          <RegionalDistributorWarning isRegionalPlanning={isRegionalPlanning} />
        )}

        {!!planningSettingsWOSummary.length && (
          <TotalCalcPlanningStandard
            key={'jbp-planning-partner-total'}
            {...props}
            planningData={planningData}
            allDataSets={hrDataSets}
            dataSetInfo={planningSettingsWOSummary[0]}
            formats={hrFormats}
            showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
            setShowActuals={(show) => setIsShowActuals(show)}
            showQuota={isShowQuota}
            setShowQuota={(show) => setIsShowQuota(show)}
            isShowFutureYearsPlans={false}
            isRegionalPlanning={isRegionalPlanning}
            setIsRegionalPlanning={(isRegionalPlanning) =>
              setIsRegionalPlanning(isRegionalPlanning)
            }
            isContractualPlanning={false}
            isContractualsAvailable={false}
            isShowOnlyCompletedPlans={isShowOnlyCompletedPlans}
            setIsShowOnlyCompletedPlans={setIsShowOnlyCompletedPlansWrapper}
            partnerFlagsState={[]}
            composedReportMode
            composedReportFYear={composedReportFYear}
            fullTotalMode
            splitType={splitType}
            setSplitType={setSplitType}
          />
        )}

        {offerings.map((offering, offeringIdx) => {
          const hrDataSetsByOfferng = hrDataSets.filter((el) => el.ProgramType === offering)
          const planningDataByOfferng = planningData.filter((el) => el.ProgramTypes === offering)
          const hrFormatsByOfferng = hrFormats.filter((el) => el.ProgramType === offering)
          const planningSettingsWOSummaryByOfferng = planningSettingsWOSummary.filter(
            (el) => el.ProgramType === offering
          )

          const isContractualsAvailable = hrDataSetsByOfferng.some(
            (el) => !!el.IsContractualsAvailable
          )
          const isContractualPlanning = isContractualsAvailable && isContractualPlanningEnable

          return (
            <>
              {!!planningSettingsWOSummaryByOfferng.length && (
                <TotalCalcPlanningStandard
                  key={`jbp-planning-offering-total-${offeringIdx}`}
                  {...props}
                  planningData={planningDataByOfferng}
                  allDataSets={hrDataSetsByOfferng}
                  dataSetInfo={planningSettingsWOSummaryByOfferng[0]}
                  formats={hrFormatsByOfferng}
                  showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
                  setShowActuals={(show) => setIsShowActuals(show)}
                  showQuota={isShowQuota}
                  setShowQuota={(show) => setIsShowQuota(show)}
                  isShowFutureYearsPlans={false}
                  isRegionalPlanning={isRegionalPlanning}
                  setIsRegionalPlanning={(isRegionalPlanning) =>
                    setIsRegionalPlanning(isRegionalPlanning)
                  }
                  isContractualPlanning={isContractualPlanning}
                  setIsContractualPlanning={(isContractualPlanning) =>
                    setIsContractualPlanningEnable(isContractualPlanning)
                  }
                  isContractualsAvailable={isContractualsAvailable}
                  isShowOnlyCompletedPlans={isShowOnlyCompletedPlans}
                  setIsShowOnlyCompletedPlans={setIsShowOnlyCompletedPlansWrapper}
                  partnerFlagsState={[]}
                  composedReportMode
                  composedReportFYear={composedReportFYear}
                  splitType={splitType}
                  setSplitType={setSplitType}
                />
              )}

              {hrDataSetsByOfferng
                .filter((d) => !d.SummaryProgram)
                .map((settings: IHRDatasetInfo, categoryIdx) => (
                  <CategoryPlanningStandard
                    key={`jbp-planning-${settings.category}-${categoryIdx}`}
                    {...props}
                    planningData={planningDataByOfferng}
                    mapData={[]}
                    dataSetInfo={settings}
                    allDataSets={hrDataSetsByOfferng}
                    formats={hrFormatsByOfferng.filter((el) => el.Category === settings.category)}
                    showActuals={isShowActuals || IS_ALWAYS_SHOW_ACTUALS}
                    setShowActuals={(show) => setIsShowActuals(show)}
                    showQuota={isShowQuota}
                    setShowQuota={(show) => setIsShowQuota(show)}
                    isShowFutureYearsPlans={false}
                    isRegionalPlanning={isRegionalPlanning}
                    setIsRegionalPlanning={(isRegionalPlanning) =>
                      setIsRegionalPlanning(isRegionalPlanning)
                    }
                    isContractualPlanning={isContractualPlanning}
                    isShowOnlyCompletedPlans={isShowOnlyCompletedPlans}
                    setIsShowOnlyCompletedPlans={setIsShowOnlyCompletedPlansWrapper}
                    onApply={(data) => onApplyChanges(data)}
                    originalCurrency={originalCurrency}
                    originalActualsValueType={originalActualsValueType}
                    partnerFlagsState={[]}
                    composedReportMode
                    composedReportFYear={composedReportFYear}
                    splitType={splitType}
                    setSplitType={setSplitType}
                    isShowComposedSoB={isShowComposedSoB}
                    setIsShowComposedSoB={setIsShowComposedSoB}
                    isShowPrevTargets={isShowPrevTargets}
                    setIsShowPrevTargets={setIsShowPrevTargets}
                  />
                ))}
            </>
          )
        })}
      </div>
    </div>
  )
})
